import {
  faArrowLeft,
  faArrowRotateRight,
  faSearch,
  faSpinner,
  faTimes
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRef, type ReactNode } from "react";

import { T, useT } from "@repo/transifex";

import { Button } from "./Button";
import { Input } from "./Inputs";
import { GridSkeleton } from "./collect/grid-skeleton";
import { ErrorDisplay } from "./collect/error-display";

type SearchPageLayoutProps<T> = {
  data?: T[];
  isLoading?: boolean;
  isError?: boolean;
  renderItem: (data: T) => ReactNode;
  onBack: () => void;
  onSearch: (search: string) => void;
  suffixElement?: ReactNode;
  onRetry?: () => void;
  emptyStateMessage: ReactNode;
  noResultsMessage: ReactNode;
};

export const SearchPageLayout = <T,>({
  data,
  renderItem,
  onBack,
  onSearch,
  suffixElement,
  isLoading,
  isError,
  onRetry,
  emptyStateMessage,
  noResultsMessage
}: SearchPageLayoutProps<T>) => {
  const t = useT();
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="h-screen px-2 py-3">
      <div className="flex h-full flex-col">
        <div className="flex gap-x-2">
          <Button
            className="font-normal text-interface-warning-4"
            icon={<FontAwesomeIcon icon={faArrowLeft} />}
            onClick={onBack}
            variant="transparent"
          >
            <T _str="Back" />
          </Button>
          <Input
            ref={inputRef}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- autofocus is needed
            autoFocus
            placeholder={t("Search")}
            className="text-xl"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              onSearch(event.target.value);
            }}
            prefixElement={
              <FontAwesomeIcon
                icon={isLoading ? faSpinner : faSearch}
                fontSize={20}
                spin={isLoading}
              />
            }
            suffixElement={
              <div className="flex items-center gap-x-2">
                {inputRef.current && inputRef.current?.value.length > 0 ? (
                  <ClearSearchButton
                    onClick={() => {
                      if (inputRef.current) {
                        inputRef.current.value = "";
                      }
                      onSearch("");
                    }}
                  />
                ) : null}
                {suffixElement}
              </div>
            }
          />
        </div>

        {isError ? (
          <SearchError onRetry={onRetry} isLoading={isLoading} />
        ) : (
          <List
            isLoading={isLoading}
            data={data}
            renderItem={renderItem}
            emptyStateMessage={emptyStateMessage}
            noResultsMessage={noResultsMessage}
          />
        )}
      </div>
    </div>
  );
};

type ListProps<T> = {
  data?: T[];
  isLoading?: boolean;
  renderItem: (data: T) => ReactNode;
  emptyStateMessage: ReactNode;
  noResultsMessage: ReactNode;
};

const List = <T,>({
  data,
  isLoading,
  renderItem,
  emptyStateMessage,
  noResultsMessage
}: ListProps<T>) => {
  if (isLoading && !data?.length) {
    return (
      <div className="h-full overflow-y-auto p-2">
        <GridSkeleton columnsCount={4} rowsCount={7} />
      </div>
    );
  }

  if (!Array.isArray(data)) {
    return (
      <div className="flex justify-center py-36">
        <h3 className="text-2xl text-interface-neutral-4">
          {emptyStateMessage}
        </h3>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex justify-center py-36">
        <h3 className="text-2xl text-interface-neutral-4">
          {noResultsMessage}
        </h3>
      </div>
    );
  }

  return (
    <div className="grid h-full auto-rows-[max(100px)] grid-cols-4 grid-rows-none gap-1 overflow-y-auto pt-2">
      {data.map(renderItem)}
    </div>
  );
};

const ClearSearchButton = (props: JSX.IntrinsicElements["button"]) => {
  return (
    <button type="button" className="text-interface-neutral-4" {...props}>
      <FontAwesomeIcon icon={faTimes} fontSize={20} />
    </button>
  );
};

const SearchError = ({
  onRetry,
  isLoading
}: {
  onRetry?: () => void;
  isLoading?: boolean;
}) => {
  return (
    <ErrorDisplay>
      <ErrorDisplay.Title>
        <T _str="Something wrong happened" />
      </ErrorDisplay.Title>
      <ErrorDisplay.Description>
        <T _str="Something went wrong with the search. Try again." />
      </ErrorDisplay.Description>
      {onRetry ? (
        <ErrorDisplay.Action
          icon={faArrowRotateRight}
          variant="neutral"
          onClick={onRetry}
          loading={isLoading}
        >
          <T _str="Retry" />
        </ErrorDisplay.Action>
      ) : null}
    </ErrorDisplay>
  );
};
